import React from 'react';

import { Skeleton, Typography, useTheme } from '@mui/material';
import { capitalCase } from 'change-case';
import { CircleAlert } from 'lucide-react';
import type { PanelVariableType } from 'src/components';
import { AlineTooltip, Spacer } from 'src/components';
import { NotStaleIcon } from 'src/components/icons/NotStaleIcon';
import { StaleIcon } from 'src/components/icons/StaleIcon';
import type { DocumentCRMField } from 'src/editor/toolbars/right-panel/integrations/hooks';
import { useUpdateIntegrationFieldsInDoc } from 'src/editor/toolbars/right-panel/integrations/hooks';
import {
  getIntegrationFieldStatus,
  integrationFieldValueMatchesDocument,
} from 'src/editor/toolbars/right-panel/integrations/integration-field-sync';
import { SyncIntegrationButton } from 'src/editor/toolbars/right-panel/integrations/IntegrationFieldButton';
import { Apollo } from 'src/gql/generated';
import { useGetCurrentDoc, useIntegrationField, useWorkflowLinkedOpportunity } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  alert: {
    backgroundColor: `rgb(235, 236, 247)`,
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  iconWrapper: {
    alignItems: 'center',
    display: 'flex',
    height: 26,
    justifyContent: 'center',
    width: 26,
  },
  label: {
    ...theme.appTypography.fieldLabel,
  },
  staleFieldActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
}));

interface IntegrationFieldAlertProps {
  variable: PanelVariableType;
}

export function IntegrationFieldAlert({
  variable,
}: IntegrationFieldAlertProps): JSX.Element | null {
  if (!variable?.integrationLink.integration || !variable?.integrationLink.attributePath)
    return null;

  return (
    <LinkedIntegrationFieldAlert
      attributePath={variable.integrationLink.attributePath}
      integration={variable.integrationLink.integration}
      variable={variable}
    />
  );
}

interface LinkedIntegrationFieldAlertProps {
  attributePath: string;
  integration: Apollo.Integration;
  variable: PanelVariableType;
}

export function LinkedIntegrationFieldAlert({
  attributePath,
  integration,
  variable,
}: LinkedIntegrationFieldAlertProps): JSX.Element | null {
  const { classes } = useStyles();
  const { document, loading: loadingDoc } = useGetCurrentDoc();
  const { linkedOpportunity, loading: loadingLinkedOpp } = useWorkflowLinkedOpportunity({
    integration,
    workflowId: document?.workflow?.id ?? null,
  });
  const { integrationField, loading: loadingIntegrationField } = useIntegrationField({
    integration,
    opportunityId: linkedOpportunity?.opportunityId ?? null,
    path: attributePath,
  });
  const pathString = attributePath
    .split('.')
    .map(pathSegment => capitalCase(pathSegment))
    .join(' - ');
  const loading = loadingDoc || loadingLinkedOpp || loadingIntegrationField;

  if (loading)
    return (
      <div className={classes.alert}>
        <div>
          <Typography className={classes.label}>Variable Connected To:</Typography>
          <Typography>
            <Skeleton variant="text" width={56} />
          </Typography>
        </div>
        <Spacer />
        <Skeleton height={26} variant="rectangular" width={26} />
      </div>
    );

  return (
    <div className={classes.alert}>
      <div>
        <Typography className={classes.label}>Variable Connected To:</Typography>
        <Typography>{`${integration} ${pathString}`}</Typography>
      </div>
      <Spacer />
      <LinkedIntegrationFieldStatus
        documentVariant={document?.variant ?? null}
        integration={integration}
        integrationField={integrationField}
        variable={variable}
      />
    </div>
  );
}

interface LinkedIntegrationFieldStatusProps {
  documentVariant: Apollo.DocumentVariant | null;
  integration: Apollo.Integration;
  integrationField: Apollo.IntegrationField | null;
  variable: PanelVariableType;
}

function LinkedIntegrationFieldStatus({
  documentVariant,
  integration,
  integrationField,
  variable,
}: LinkedIntegrationFieldStatusProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const documentCRMField = integrationField
    ? ({
        ...integrationField,
        fieldInDocInfo: {
          ...variable,
          assigneeId: variable.action?.assignee?.id ?? null,
          integration,
          isStale: !integrationFieldValueMatchesDocument(variable.value, integrationField.value, [
            integrationField.title,
            variable.label,
          ]),
        },
      } satisfies DocumentCRMField)
    : null;
  const { updateIntegrationFieldsInDoc } = useUpdateIntegrationFieldsInDoc();
  const isTemplate = documentVariant === Apollo.DocumentVariant.Template;
  const status = getIntegrationFieldStatus({
    hasIntegrationField: Boolean(documentCRMField),
    isStale: documentCRMField?.fieldInDocInfo.isStale ?? false,
    isTemplate,
    valueStatus: documentCRMField?.valueStatus,
  });

  if (status === 'unavailable' || !documentCRMField)
    return (
      <AlineTooltip arrow placement="top" title={`Unable to load value from ${integration}`}>
        <div className={classes.iconWrapper}>
          <CircleAlert size={18} />
        </div>
      </AlineTooltip>
    );

  if (status === 'valueUnavailable')
    return (
      <AlineTooltip arrow placement="top" title={`Failed to load value from ${integration}`}>
        <div className={classes.iconWrapper}>
          <CircleAlert color={theme.palette.error.main} size={18} />
        </div>
      </AlineTooltip>
    );

  if (status === 'addedToTemplate' || status === 'synced')
    return (
      <AlineTooltip
        arrow
        placement="top"
        title={
          isTemplate
            ? `${integration} variable added to template`
            : `Variable synced with ${integration}`
        }
      >
        <div className={classes.iconWrapper}>
          <NotStaleIcon />
        </div>
      </AlineTooltip>
    );

  return (
    <div className={classes.staleFieldActions}>
      <SyncIntegrationButton
        buttonProps={{
          onClick: () => {
            updateIntegrationFieldsInDoc([documentCRMField]);
          },
        }}
        title={`Sync variable with ${integration}`}
      />
      <AlineTooltip arrow placement="top" title={`Variable out of sync with ${integration}`}>
        <div className={classes.iconWrapper}>
          <StaleIcon />
        </div>
      </AlineTooltip>
    </div>
  );
}
